import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SearchItem } from '../components/widgets/weather-widget/components/search-results/search-results.model';
import { Location, LocationsQueryResponse, UserLocation } from '../models/location.model';
import { MAX_LOCATION_SUGGESTIONS, MIN_SEARCH_TERM_LENGTH } from '../shared/constants/search.constants';
import { LOCATIONS_QUERY } from '../shared/graphql/locations.query';
import { ApiEndpointsService } from './api-endpoints.service';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiEndpointsService);

  /**
   * Search for locations based on a search string
   * @param search The search string
   * @returns Observable of Location array
   */
  private searchLocations(search: string): Observable<Location[]> {
    if (!search || search.trim().length < MIN_SEARCH_TERM_LENGTH) {
      return of([]);
    }

    const query = LOCATIONS_QUERY;
    const variables = { search: search.trim() };

    // Mock data for testing - uncomment below to use real API
    // return of(MOCK_LOCATIONS).pipe(delay(500));

    return this.http.post<LocationsQueryResponse>(this.apiService.baseUrl, { query, variables }, { withCredentials: true }).pipe(
      map((response) => {
        const locations = response?.data?.locations ?? [];
        console.log('API search results:', locations);
        // Limit results to 5-10 as per requirements
        return locations.slice(0, MAX_LOCATION_SUGGESTIONS);
      }),
      catchError((err) => {
        console.error('Failed to search locations', err);
        return of<Location[]>([]);
      })
    );
  }

  getLocationSuggestions(term: string): Observable<SearchItem[]> {
    if (!term || term.trim().length < MIN_SEARCH_TERM_LENGTH) {
      return of([]);
    }

    return this.searchLocations(term).pipe(
      map((locations) =>
        locations.map((location, index) => ({
          id: location.key || index.toString(),
          label: `${location.name}, ${location.AdministrativeArea}, ${location.country}`,
          location: this.toUserLocation(location),
        }))
      )
    );
  }

  /**
   * Convert Location to UserLocation
   * @param location Location from API (contains key for location_id)
   * @returns UserLocation with location_id
   */
  toUserLocation(location: Location): UserLocation {
    return {
      key: location.key,
      name: location.name,
      country: location.country,
    };
  }
}
